/**
 * Axis renderer.
 * Draws the dynamic Y-axis ticks on the right side.
 */

import { DEFAULT_CHART_CONFIG } from '../config/constants';
import type { ThemeConfig, LayoutConfig, PriceConfig } from '../config/types';
import { DEFAULT_PRICE_CONFIG } from '../utils/priceFormat';

import type { SVGSelection, YScale, SelectionsCache } from './types';

/**
 * Draws the dynamic Y-axis ticks on the right side.
 *
 * @param svg - D3 SVG selection
 * @param yScale - Y-axis scale function
 * @param width - Chart width in pixels
 * @param theme - Theme configuration (uses defaults if not provided)
 * @param layout - Layout configuration (uses defaults if not provided)
 * @param priceConfig - Price formatting configuration (uses defaults if not provided)
 * @param cache - Optional cached D3 selections (avoids DOM queries)
 */
export const renderAxis = (
    svg: SVGSelection,
    yScale: YScale,
    width: number,
    theme: ThemeConfig = DEFAULT_CHART_CONFIG.theme,
    layout: LayoutConfig = DEFAULT_CHART_CONFIG.layout,
    priceConfig: PriceConfig = DEFAULT_PRICE_CONFIG,
    cache?: SelectionsCache,
): void => {
    const { axisText } = theme;
    const { axisTickCount, axisFontSize } = layout;

    // Use cached selection if available, otherwise query DOM
    const axisGroup = cache?.yAxisLabels ?? svg.select('.y-axis-labels');
    const ticks = yScale.ticks(axisTickCount);

    // D3 Data Join
    const labels = axisGroup
        .selectAll<SVGTextElement, number>('text')
        .data(ticks, (d) => d);

    const xRight = width - 16;
    labels
        .enter()
        .append('text')
        .attr('text-anchor', 'end')
        .attr('alignment-baseline', 'middle')
        .attr('class', 'font-mono select-none')
        .merge(labels)
        .attr('x', xRight)
        .attr('font-size', axisFontSize)
        .attr('fill', axisText)
        .attr('y', (d) => yScale(d))
        .text((d) => priceConfig.formatTick(d));

    labels.exit().remove();
};
